// src/components/DepartmentRow.js
import React from "react";
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  TextField,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Button,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import LayersIcon from "@mui/icons-material/Layers";
import RoomRow from "./RoomRow";
import {
  textFieldSx,
  formatArea,
  formatAreaInput,
  toInt,
} from "./utils/tableHelpers";
import { useInternalToggle } from "../hooks/useToggle";

export default function DepartmentRow({
  department,
  roomsByDepartment,
  departmentTypes = [],
  roomTypes = [],
  floorTypes = [],
  onSaveDepartment,
  onSaveRoom,
  onCancel,
  forceEdit = false,
}) {
  const { isOpen, toggle } = useInternalToggle(false);
  const [editing, setEditing] = React.useState(forceEdit);
  const [editData, setEditData] = React.useState(department);
  const [tempRooms, setTempRooms] = React.useState([]);

  const rooms = roomsByDepartment[department.pk_id] || [];

  React.useEffect(() => setEditData(department), [department]);
  React.useEffect(() => {
    if (forceEdit) setEditing(true);
  }, [forceEdit]);

  const handleEdit = () => setEditing(true);
  const handleCancel = () => {
    if (onCancel && department.isNew) {
      onCancel(); // Remove temporary department
    } else {
      setEditData(department);
      setEditing(false);
    }
  };
  const handleSave = () => {
    onSaveDepartment?.(editData);
    setEditing(false);
  };
  const handleChange = (field, value) =>
    setEditData((prev) => ({ ...prev, [field]: value }));

  // Room handlers
  const handleCreateRoom = () => {
    const newTempRoom = {
      tempId: `temp-${Date.now()}`,
      pk_id: null,
      room_name: "",
      department_pk_id: department.pk_id,
      room_area: "",
      room_baseline_cleaning_area: "",
      isNew: true,
    };
    setTempRooms((prev) => [...prev, newTempRoom]);
  };

  const handleSaveTempRoom = (roomData) => {
    // Remove temp fields and save
    const { tempId, isNew, ...cleanData } = roomData;
    onSaveRoom?.(cleanData);
    // Remove from temp rooms
    setTempRooms((prev) => prev.filter((r) => r.tempId !== tempId));
  };

  const handleCancelTempRoom = (tempId) => {
    setTempRooms((prev) => prev.filter((r) => r.tempId !== tempId));
  };

  return (
    <>
      <TableRow
        hover
        onClick={toggle}
        sx={{
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <TableCell padding="checkbox">
          <IconButton size="small">
            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 500 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title={editData.department_name || ""}>
              <span
                style={{
                  display: "inline-block",
                  maxWidth: 360,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {editData.department_name || "Unnamed department"}
              </span>
            </Tooltip>
            {editData.department_floor != null && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 1 }}
              >
                <LayersIcon sx={{ fontSize: 16, color: "#666" }} />
                <Typography variant="caption" sx={{ color: "#666" }}>
                  {editData.department_floor}
                </Typography>
              </Box>
            )}
            {rooms.length > 0 && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 1 }}
              >
                <MeetingRoomIcon sx={{ fontSize: 16, color: "#666" }} />
                <Typography variant="caption" sx={{ color: "#666" }}>
                  {rooms.length}
                </Typography>
              </Box>
            )}
          </Box>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={2}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Box sx={{ ml: 6, my: 1, marginLeft: 0 }}>
              <Card
                variant="outlined"
                sx={{ mb: 2, backgroundColor: "#f9fafb" }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="subtitle2" color="primary">
                      Department Details
                    </Typography>
                    {!editing ? (
                      <IconButton size="small" onClick={handleEdit}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <Box>
                        <IconButton
                          size="small"
                          onClick={handleSave}
                          color="success"
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancel}
                          color="error"
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>

                  {!editing ? (
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 4, md: 4 }}>
                        <Typography variant="caption" color="text.secondary">
                          Name
                        </Typography>
                        <Typography variant="body2">
                          {editData.department_name || "-"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 1, md: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Floor
                        </Typography>
                        <Typography variant="body2">
                          {editData.department_floor ?? "-"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 1, md: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Area
                        </Typography>
                        <Typography variant="body2">
                          {formatArea(editData.department_area)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 3, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                          Baseline Cleaning Area
                        </Typography>
                        <Typography variant="body2">
                          {formatArea(
                            editData.department_baseline_cleaning_area,
                          )}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 3, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                          Type
                        </Typography>
                        <Typography variant="body2">
                          {editData.department_type_id
                            ? departmentTypes.find(
                                (t) => t.pk_id === editData.department_type_id,
                              )?.type_name ||
                              `Unknown Type (ID: ${editData.department_type_id})`
                            : "-"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 12 }}>
                        <Typography variant="caption" color="text.secondary">
                          Description
                        </Typography>
                        <Typography variant="body2">
                          {editData.department_description || "-"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 3, md: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Access Control
                        </Typography>
                        <Typography variant="body2">
                          {editData.department_access_control ? "Yes" : "No"}
                        </Typography>
                      </Grid>
                      {editData.department_access_details ? (
                        <Grid size={{ xs: 9, md: 10 }}>
                          <Typography variant="caption" color="text.secondary">
                            Access Details
                          </Typography>
                          <Typography variant="body2">
                            {editData.department_access_details}
                          </Typography>
                        </Grid>
                      ) : null}
                    </Grid>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 4, md: 4 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          Name
                        </Typography>
                        <TextField
                          size="small"
                          value={editData.department_name || ""}
                          onChange={(e) =>
                            handleChange("department_name", e.target.value)
                          }
                          fullWidth
                          sx={textFieldSx}
                        />
                      </Grid>
                      <Grid size={{ xs: 1, md: 1 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          Floor
                        </Typography>
                        <TextField
                          size="small"
                          type="number"
                          value={editData.department_floor ?? ""}
                          onChange={(e) =>
                            handleChange(
                              "department_floor",
                              toInt(e.target.value),
                            )
                          }
                          fullWidth
                          sx={textFieldSx}
                        />
                      </Grid>
                      <Grid size={{ xs: 1, md: 1 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          Area (m²)
                        </Typography>
                        <TextField
                          disabled
                          size="small"
                          type="number"
                          value={formatAreaInput(editData.department_area)}
                          onChange={(e) =>
                            handleChange(
                              "department_area",
                              toInt(e.target.value),
                            )
                          }
                          fullWidth
                          sx={{
                            ...textFieldSx,
                            "& .MuiInputBase-root.Mui-disabled": {
                              backgroundColor: "#f0f0f0", // Light gray
                              // backgroundColor: "#e0e0e0", // Darker gray
                              // backgroundColor: "#d5d5d5", // Even darker
                            },
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "#666", // Text color for disabled
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 3, md: 3 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          Baseline Cleaning Area (m²)
                        </Typography>
                        <TextField
                          disabled
                          size="small"
                          type="number"
                          value={formatAreaInput(
                            editData.department_baseline_cleaning_area,
                          )}
                          onChange={(e) =>
                            handleChange(
                              "department_baseline_cleaning_area",
                              toInt(e.target.value),
                            )
                          }
                          fullWidth
                          sx={{
                            ...textFieldSx,
                            "& .MuiInputBase-root.Mui-disabled": {
                              backgroundColor: "#f0f0f0", // Light gray
                              // backgroundColor: "#e0e0e0", // Darker gray
                              // backgroundColor: "#d5d5d5", // Even darker
                            },
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "#666", // Text color for disabled
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 3, md: 3 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          Department Type
                        </Typography>
                        <TextField
                          size="small"
                          select
                          value={editData.department_type_id ?? ""}
                          onChange={(e) =>
                            handleChange(
                              "department_type_id",
                              e.target.value ? parseInt(e.target.value) : null,
                            )
                          }
                          fullWidth
                          sx={textFieldSx}
                        >
                          <MenuItem value="">Select Type</MenuItem>
                          {departmentTypes.map((type) => (
                            <MenuItem key={type.pk_id} value={type.pk_id}>
                              {type.type_name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid size={{ xs: 12, md: 12 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          Description
                        </Typography>
                        <TextField
                          size="small"
                          value={editData.department_description || ""}
                          onChange={(e) =>
                            handleChange(
                              "department_description",
                              e.target.value,
                            )
                          }
                          fullWidth
                          sx={textFieldSx}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 12 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!editData.department_access_control}
                              onChange={(e) =>
                                handleChange(
                                  "department_access_control",
                                  e.target.checked ? 1 : 0,
                                )
                              }
                              size="small"
                            />
                          }
                          label="Access control in place"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 12 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          Access Details
                        </Typography>
                        <TextField
                          size="small"
                          value={editData.department_access_details || ""}
                          onChange={(e) =>
                            handleChange(
                              "department_access_details",
                              e.target.value,
                            )
                          }
                          fullWidth
                          sx={textFieldSx}
                        />
                      </Grid>
                    </Grid>
                  )}
                </CardContent>
              </Card>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Rooms
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleCreateRoom}
                  sx={{ minWidth: "auto", fontSize: "0.75rem" }}
                >
                  Add Room
                </Button>
              </Box>
              {rooms.length === 0 && tempRooms.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No rooms
                </Typography>
              ) : (
                <Table size="small" aria-label="rooms table">
                  <TableBody>
                    {rooms.map((r) => (
                      <RoomRow
                        key={r.pk_id}
                        room={r}
                        onSaveRoom={onSaveRoom}
                        roomTypes={roomTypes}
                        floorTypes={floorTypes}
                      />
                    ))}
                    {tempRooms.map((r) => (
                      <RoomRow
                        key={r.tempId}
                        room={r}
                        onSaveRoom={handleSaveTempRoom}
                        roomTypes={roomTypes}
                        floorTypes={floorTypes}
                        forceEdit={true}
                        onCancel={() => handleCancelTempRoom(r.tempId)}
                      />
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
